// Third-party
import React, { useState } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';

// Componets
import LegacyContactDetailsInfo from './LegacyContactDetailsInfo';
import { EditContactModal } from '@/views/components/modals';
import { EntityDetailsSummary } from '@/views/components/common/EntityDetailsSummary';
import { ContactPastille, PillLabel } from '../../../components/misc';

// Types
import GenericContact from '@/types/generic-contact';
import Locale from '@/types/locale';
import User from '@/types/user';
import { CONTACTS_LOCALE, formatMessage } from '@/utils/i18n';
import { Action, BasicInfo, EntityDetails, Pill } from '@/types/EntityDetails';

// Utils
import useThemeVersion from '@/hooks/useThemeVersion';
import { BMOi18n } from '@/utils/bmo-gif/constants';
import { getAge } from '@/utils/time';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

// Redux
import { loadContactForEdit } from '@/redux/modules/contacts';

type DispatchProps = {
  loadContactForEdit: (contact: GenericContact) => void;
};

type Props = DispatchProps & {
  contact: GenericContact;
  hasDisplayedActions?: boolean;
  isGIF: boolean;
  locale: Locale;
  user: User;

  onEdit?: () => void;
  onDelete?: () => void;
};

export const ContactDetailsInfoV2 = (props: Props) => {
  const { contact, isGIF, user, loadContactForEdit, onDelete } = props;
  const [showEditContactModal, setShowEditContactModal] = useState<boolean>(false);

  const hideEditContactModal = () => {
    setShowEditContactModal(false);
  };

  const onEditContact = () => {
    loadContactForEdit(contact);
    setShowEditContactModal(true);
  };

  const onContactSave = () => {
    setShowEditContactModal(false);
  };

  const renderContactBasicInfo = (): BasicInfo[] => {
    const hasContactLastName = userHasPermission(props.user, PERMISSIONS.CONTACTS_LAST_NAME);
    const hasContactEmail = userHasPermission(props.user, PERMISSIONS.CONTACTS_EMAIL);

    return [
      {
        show: true,
        value: `${contact.fname} ${hasContactLastName ? contact.lname : ''}`,
        className: 'name',
      },
      { show: hasContactEmail, value: contact.email, className: 'email' },
    ];
  };

  const renderContactPill = (): Pill[] => {
    const hasContactType = userHasPermission(props.user, PERMISSIONS.CONTACTS_TYPE) && contact.contactType != null;
    const hasContactStage = userHasPermission(props.user, PERMISSIONS.CONTACTS_STAGE) && contact.stage != null;
    const hasPill = hasContactType || hasContactStage;

    if (!hasPill) {
      return [];
    }

    return [
      {
        show: hasContactType,
        value: <PillLabel className="pill" text={formatMessage(`Contacts.ContactTypes.${contact.contactType}`)} />,
        key: 'contactType',
      },
      {
        show: hasContactStage,
        value: <PillLabel className="pill" text={formatMessage(`Contacts.ContactStages.${contact.stage}`)} />,
        key: 'contactStage',
      },
    ];
  };

  const renderLanguage = () => {
    const { contact } = props;
    const formattedLocale = CONTACTS_LOCALE.find(locale => locale.value === contact.locale)?.label || '--';

    return formattedLocale;
  };

  const renderDetails = (): EntityDetails[] => {
    const { contact } = props;
    const hasContactLocale = userHasPermission(props.user, PERMISSIONS.CONTACTS_LOCALE) && contact.locale;

    return [
      {
        show: !!contact.uid,
        value: contact.uid,
        i18nPath: 'ContactDetails.Overview.ClientID',
        key: 'clientID',
      },
      {
        show: !!hasContactLocale,
        value: renderLanguage(),
        i18nPath: 'Contacts.ContactDetailsInfo.Locale',
        key: 'locale',
      },
    ];
  };

  const renderGIFDetails = (): EntityDetails[] => {
    const { contact, locale } = props;
    const messages = BMOi18n[locale];
    const { DOB, AGE, YES, NO, POLICYOWNER_ANNUITANT_OPTION, POLICYOWNER_1, POLICYOWNER_2, ACCOUNT_TYPE, PROVINCE } = messages;

    const policyOwnerOne = contact.fnamePolicyOwner && `${contact.fnamePolicyOwner} ${contact.lnamePolicyOwner}`;
    const policyOwnerTwo = contact.fnameSecondPolicyOwner && `${contact.fnameSecondPolicyOwner} ${contact.lnameSecondPolicyOwner}`;

    return [
      {
        show: !!contact.dob,
        value: moment(contact.dob).format(formatMessage('Common.DateFormats.Long')),
        i18nPath: DOB,
        key: 'dob',
      },
      {
        show: !!contact.dob,
        value: contact.dob && getAge(contact.dob),
        i18nPath: AGE,
        key: 'age',
      },
      {
        show: true,
        value: contact.ownerIsAnnuitant ? YES : NO,
        i18nPath: POLICYOWNER_ANNUITANT_OPTION,
        key: 'owner',
      },
      {
        show: !!policyOwnerOne,
        value: policyOwnerOne,
        i18nPath: POLICYOWNER_1,
        key: 'policyOwnerOne',
      },
      {
        show: !!policyOwnerTwo,
        value: policyOwnerTwo,
        i18nPath: POLICYOWNER_2,
        key: 'policyOwnerTwo',
      },
      {
        show: true,
        value: `${contact.accountType} ${contact.registeredAccountType ? `, ${contact.registeredAccountType}` : ''}`,
        i18nPath: ACCOUNT_TYPE,
        key: 'accountType',
      },
      {
        show: !!contact.province,
        value: contact.province,
        i18nPath: PROVINCE,
        key: 'province',
      },
    ];
  };

  const renderActions = (): Action[] => {
    const { hasDisplayedActions } = props;

    const editForcedFalse = !contact.availableActions?.edit;
    const deleteForcedFalse = !contact.availableActions?.delete;

    if (hasDisplayedActions === false) {
      return [];
    }

    return [
      {
        className: 'edit-contact-btn',
        forcedFalse: editForcedFalse,
        i18nPath: 'ContactDetails.Overview.EditContact',
        onClick: onEditContact,
        permissions: [PERMISSIONS.CONTACTS_EDIT],
      },
      {
        className: 'delete-contact-btn',
        forcedFalse: deleteForcedFalse,
        i18nPath: 'ContactDetails.Overview.DeleteContact',
        onClick: () => onDelete && onDelete(),
        permissions: [PERMISSIONS.CONTACTS_DELETE],
      },
    ];
  };

  return (
    <>
      <EntityDetailsSummary
        actions={renderActions()}
        entityBasicInfo={renderContactBasicInfo()}
        entityPills={renderContactPill()}
        entityDetails={isGIF ? renderGIFDetails() : renderDetails()}
        icon={<ContactPastille contact={contact} />}
      />
      <EditContactModal user={user} show={showEditContactModal} onCancel={hideEditContactModal} onSave={onContactSave} contact={contact} isEditing />
    </>
  );
};

const ContactDetailsInfo: React.FC<React.ComponentProps<typeof ContactDetailsInfoV2>> = props => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ContactDetailsInfoV2 {...props} />;
  }
  return <LegacyContactDetailsInfo onDelete={() => {}} onEdit={() => {}} {...props} />;
};

export default connect(null, {
  loadContactForEdit,
})(ContactDetailsInfo);
